import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MagnifyingGlassIcon,
  BookOpenIcon,
  TagIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Footer from '../components/common/Footer';
import SiteHeader from '../components/common/SiteHeader';

interface GlossaryTerm {
  _id: string;
  term: string;
  slug: string;
  abbreviation?: string;
  shortDefinition: string;
  fullDefinition: string;
  category: string;
  examples?: Array<{
    text: string;
    explanation: string;
  }>;
  relatedTerms?: string[];
  tags?: string[];
}

const Glossary: React.FC = () => {
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<GlossaryTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  const categories = [
    'all',
    'Exercise Types',
    'Financial Terms',
    'Legal Terms',
    'Trading Terms',
    'Compliance',
    'Documentation',
    'Market Structure',
    'Regulatory',
    'Securities',
    'Corporate Structure'
  ];

  useEffect(() => {
    fetchTerms();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    filterTerms();
  }, [searchTerm, selectedCategory, terms]);

  const handleScroll = () => {
    setShowScrollTop(window.pageYOffset > 300);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get('/api/glossary/terms');
      if (response.data.success) {
        setTerms(response.data.terms);
      }
    } catch (error) {
      console.error('Error fetching glossary terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTerms = () => {
    if (!terms || !Array.isArray(terms)) {
      setFilteredTerms([]);
      return;
    }
    
    let filtered = [...terms];

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(term => 
        term.term.toLowerCase().includes(search) ||
        term.abbreviation?.toLowerCase().includes(search) ||
        term.shortDefinition.toLowerCase().includes(search) ||
        term.fullDefinition.toLowerCase().includes(search) ||
        term.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(term => term.category === selectedCategory);
    }

    setFilteredTerms(filtered);
  };

  const groupTermsByLetter = (terms: GlossaryTerm[]) => {
    const grouped: { [key: string]: GlossaryTerm[] } = {};
    
    if (!terms || !Array.isArray(terms)) {
      return grouped;
    }
    
    terms.forEach(term => {
      const firstLetter = term.term[0].toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(term);
    });

    // Sort terms within each letter group
    Object.keys(grouped).forEach(letter => {
      grouped[letter].sort((a, b) => a.term.localeCompare(b.term));
    });

    return grouped;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Exercise Types': 'bg-blue-100 text-blue-800',
      'Financial Terms': 'bg-green-100 text-green-800',
      'Legal Terms': 'bg-purple-100 text-purple-800',
      'Trading Terms': 'bg-orange-100 text-orange-800',
      'Compliance': 'bg-red-100 text-red-800',
      'Documentation': 'bg-yellow-100 text-yellow-800',
      'Market Structure': 'bg-indigo-100 text-indigo-800',
      'Regulatory': 'bg-pink-100 text-pink-800',
      'Securities': 'bg-teal-100 text-teal-800',
      'Corporate Structure': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <>
        <SiteHeader />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </>
    );
  }

  const groupedTerms = groupTermsByLetter(filteredTerms || []);
  const letters = Object.keys(groupedTerms).sort();

  return (
    <>
      <SiteHeader />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Glossary of Terms
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Comprehensive definitions for equity financing terminology and legal terms
            </motion.p>

            {/* Search Bar */}
            <motion.div 
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search terms, definitions, or abbreviations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pr-12 text-lg rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <MagnifyingGlassIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-orange-500 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Categories' : category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-orange-500">{terms?.length || 0}</div>
                  <div className="text-gray-600">Total Terms</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-500">{filteredTerms?.length || 0}</div>
                  <div className="text-gray-600">Filtered Results</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-500">{categories.length - 1}</div>
                  <div className="text-gray-600">Categories</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Terms List */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Terms Display */}
            {filteredTerms && filteredTerms.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No terms found matching your search.</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="mt-4 text-orange-500 hover:text-orange-600 font-medium"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {letters.map(letter => (
                  <motion.div
                    key={letter}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                  >
                    {/* Letter Header */}
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3">
                      <h2 className="text-2xl font-bold text-white">{letter}</h2>
                    </div>

                    {/* Terms */}
                    <div className="divide-y divide-gray-100">
                      {groupedTerms[letter].map(term => (
                        <motion.div
                          key={term._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-6 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-semibold text-gray-900">
                                  {term.term}
                                </h3>
                                {term.abbreviation && (
                                  <span className="text-sm text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                                    {term.abbreviation}
                                  </span>
                                )}
                                <span className={`text-xs px-3 py-1 rounded-full font-medium ${getCategoryColor(term.category)}`}>
                                  {term.category}
                                </span>
                              </div>

                              <p className="text-gray-700 mb-3">{term.shortDefinition}</p>

                              {expandedTerm === term._id ? (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="space-y-4"
                                >
                                  <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-2">Full Definition</h4>
                                    <p className="text-gray-700 leading-relaxed">{term.fullDefinition}</p>
                                  </div>

                                  {term.examples && term.examples.length > 0 && (
                                    <div className="bg-orange-50 rounded-lg p-4">
                                      <h4 className="font-semibold text-gray-900 mb-2">Examples</h4>
                                      {term.examples.map((example, idx) => (
                                        <div key={idx} className="mb-3 last:mb-0">
                                          <p className="text-gray-700 font-medium">{example.text}</p>
                                          <p className="text-gray-600 text-sm mt-1">→ {example.explanation}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {term.relatedTerms && term.relatedTerms.length > 0 && (
                                    <div>
                                      <h4 className="font-semibold text-gray-900 mb-2">Related Terms</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {term.relatedTerms.map((related, idx) => (
                                          <button
                                            key={idx}
                                            onClick={() => setSearchTerm(related)}
                                            className="text-orange-500 hover:text-orange-600 font-medium text-sm flex items-center gap-1"
                                          >
                                            {related}
                                            <ChevronRightIcon className="h-3 w-3" />
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {term.tags && term.tags.length > 0 && (
                                    <div className="flex items-center gap-2 pt-2">
                                      <TagIcon className="h-4 w-4 text-gray-400" />
                                      <div className="flex flex-wrap gap-2">
                                        {term.tags.map((tag, idx) => (
                                          <span key={idx} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                              ) : null}

                              <button
                                onClick={() => setExpandedTerm(expandedTerm === term._id ? null : term._id)}
                                className="mt-3 text-orange-500 hover:text-orange-600 font-medium text-sm flex items-center gap-1"
                              >
                                {expandedTerm === term._id ? 'Show Less' : 'Show More'}
                                <ChevronRightIcon className={`h-4 w-4 transform transition-transform ${
                                  expandedTerm === term._id ? 'rotate-90' : ''
                                }`} />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-orange-500 text-white p-4 rounded-full shadow-lg hover:bg-orange-600 transition-colors z-40"
          >
            <ArrowUpIcon className="h-6 w-6" />
          </motion.button>
        )}

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary-950 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-primary-700 mb-8">
              Our team is here to help you understand equity financing and find the right solution for your company.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Contact Our Experts
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Glossary; 